/**
 * Use My League: the league picker and redirect, fetching league data, recalculating player
 * values in the worker (or the main-thread WASM fallback), and writing the results into rows.
 *
 * Calls on sibling mixins via `this`: Filtering (selectedFantasyPosition), Sorting (refreshTable,
 * _parkedRows), CellClasses (dsHelper), MobileTableChrome (refreshMobileTableChrome), UrlAndScoring
 * (pprScoring, superflex, selectedWeek, currentWeek), CoreState (basePath, scoringModalOpen,
 * loadingText).
 */
import Alpine from '/js/alpine/core@3.14.9/alpine.esm.js';
import RankingsWorkerManager from './../modules/RankingsWorkerManager.js';
import { getArrItemByPropValue } from './../../helpers/DsHelper.js';
import { getPickDisplay, TYPE_ID_AUCTION } from './../../helpers/DraftHelper.js';
import { assignMissingDefaultPlayerDataValues } from '../../league/helpers/DsValue.js';
import { assignMissingDefaultMvpBoardScoringValues, getRosterSpotsInLeague } from './../../helpers/MvpBoardScoringHelper.js';
import { OFFENSIVE_FLEX_ROSTER_SPOTS } from './../../helpers/RosterSpotHelper.js';
import LocalStorage from './../../helpers/LocalStorage.js';
import { DEBUG_MODE_DISABLED } from './../../helpers/DebugHelper.js';
import { exportAndPublishWasmLogEntries } from './../../helpers/WasmLogHelper.js';
import { DEFAULT_LOADING_TEXT, ROSTER_STATUS_BASE_PATHS } from './RankingsConstants.js';

export default () => ({
    leagueDropdownOpen: false,
    // Worker and processing state
    workerManager: null,
    isProcessing: false,
    processingMessage: '',
    leagueType: null,
    userLeagues: [],
    get groupedUserLeagues() {
        let groupedLeagues = this.userLeagues.reduce((groups, league) => {
            const groupKey = league.league_type.charAt(0).toUpperCase() + league.league_type.slice(1).toLowerCase();
            if (!groups[groupKey]) {
                groups[groupKey] = [];
            }
            groups[groupKey].push(league);
            return groups;
        }, {});

        const topLeagueType = this.basePath === 'auction-values' ? 'Redraft' : this.leagueType;
        if (topLeagueType && groupedLeagues[topLeagueType]) {
            groupedLeagues = {
                [topLeagueType]: groupedLeagues[topLeagueType],
                ...Object.fromEntries(Object.entries(groupedLeagues).filter(([k]) => k !== topLeagueType)),
            };
        }

        return groupedLeagues;
    },
    selectedUserLeague: null,
    selectedUserLeagueId: null,
    selectedUserLeagueText: 'Use My League',
    cachedLeagueData: {},

    // Promise for the league calculation in flight; the player modal awaits it
    _userLeagueDataPromise: null,

    initRedirectedUserLeague() {
        const rk = `redirected${this.leagueType}SelectedUserLeagueId`;
        const redirectedSelectedUserLeagueId = LocalStorage.get(rk, null);
        if (redirectedSelectedUserLeagueId) {
            this.selectedUserLeagueId = redirectedSelectedUserLeagueId;
            this.handleUserLeagueChange();
            LocalStorage.set(rk, null); // Clear the redirected league ID after using it
        }
    },

    initRosterStatusEffect() {
        // The roster status modifier widens the rank column for the Mine / Free label strip,
        // so the mobile sticky header clone has to re-measure whenever it flips
        Alpine.effect(() => {
            const showsRosterStatus = this.showsRosterStatus();
            if (this.$root.classList.contains('rankings-page--roster-status') === showsRosterStatus) {
                return;
            }
            this.$root.classList.toggle('rankings-page--roster-status', showsRosterStatus);
            Alpine.nextTick(() => this.refreshMobileTableChrome());
        });
    },

    // The constructor runs synchronously; ensureDsValueForUserLeague relies on workerManager
    // existing before the first await in the root init()
    async initWorker() {
        // Initialize worker manager - this returns a boolean indicating success
        this.workerManager = new RankingsWorkerManager();
        await this.workerManager.init();
    },

    /**
     * Whether a user league is selected on the page
     * @returns {boolean}
     */
    hasUserLeagueSelected() {
        return Boolean(this.selectedUserLeagueId) && this.selectedUserLeagueId !== 'none' && this.selectedUserLeagueId !== 'sync-now';
    },

    /**
     * Whether player rows flag their roster status (Mine / Free) in the selected user league
     *
     * @returns {boolean}
     */
    showsRosterStatus() {
        return ROSTER_STATUS_BASE_PATHS.includes(this.basePath) && this.hasUserLeagueSelected();
    },

    getIsPositionInLeague(position) {
        // --- For default leagues we show all the roster spots EXCEPT for the position-specific
        // --- flex spots (we show FLEX) instead
        const cachedUserLeagueData = this.cachedLeagueData[this.selectedUserLeagueId];
        if (cachedUserLeagueData === undefined) {
            return !OFFENSIVE_FLEX_ROSTER_SPOTS.includes(position);
        }

        // --- For user leagues we show all roster spots for the league, with the unique
        // --- case for IDP since it's value id 'idp' here instead of D|L|D
        const mvpBoardScoring = cachedUserLeagueData.leagueData.mvpBoardScoring;
        const rosterSpotsInLeague = getRosterSpotsInLeague(mvpBoardScoring);
        return (position.toLowerCase() == 'idp' && mvpBoardScoring.starting_idp_flex > 0) || rosterSpotsInLeague.includes(position);
    },

    handleUserLeagueChange() {
        if (!this.selectedUserLeagueId || this.selectedUserLeagueId === 'none') {
            return;
        }
        if (this.selectedUserLeagueId === 'sync-now') {
            window.location = '/league/mvp/scoring';
            return;
        }
        this.selectedUserLeague = this.userLeagues.find(league => league.id == this.selectedUserLeagueId);
        if (!this.selectedUserLeague) {
            console.warn('Selected user league not found:', this.selectedUserLeagueId);
            return;
        }

        // Redirect to the appropriate rankings page if the
        // selected league is not for this page
        if (this.shouldRedirectForUserLeague()) {
            this.redirectForUserLeague();
            return;
        }

        this.loadingText = DEFAULT_LOADING_TEXT;
        this.scoringModalOpen = true;
        this.selectedUserLeagueText = this.selectedUserLeague.league_name || 'Use My League';
        const selectedUserScoring = window.appData?.scoringConfigurations?.find(
            scoringConfig => scoringConfig.id === `mvpBoardScoringId${this.selectedUserLeagueId}`
        );
        if (selectedUserScoring) {
            this.pprScoring = selectedUserScoring.pprScoring || '';
            this.superflex = selectedUserScoring.superflex || false;
        }

        this.isProcessing = true;
        this.processingMessage = 'Calculating league values...';

        // Make a fetch request to the server to get the leage data. The promise is kept for
        // the player modal, which waits on it when opened mid-calculation.
        this._userLeagueDataPromise = this.ensureDsValueForUserLeague(this.selectedUserLeagueId);
        this._userLeagueDataPromise
            .then(_json => {
                this.refreshTable();
            })
            .catch(error => {
                console.error('Error fetching league data:', error);
            })
            .finally(() => {
                this.scoringModalOpen = false;
                this.isProcessing = false;
                this.processingMessage = '';
            });
    },

    /**
     * Redirect to the appropriate rankings page for the selected user league
     * based on its league_type and draft_type_id. Stores the selected league ID
     * in localStorage to remember the choice after redirect.
     * For auction leagues, redirect to /auction-values
     * For redraft leagues, redirect to /rankings
     * For dynasty leagues, redirect to /dynasty-rankings
     * For keeper leagues, redirect to /keeper-rankings
     * @returns {void}
     */
    redirectForUserLeague() {
        const leagueTypeSlug = this.selectedUserLeague.league_type.toLowerCase();
        let url = `/${leagueTypeSlug}-rankings`;
        if (this.selectedUserLeague.draft_type_id === TYPE_ID_AUCTION) {
            url = '/auction-values';
        } else if (leagueTypeSlug === 'redraft') {
            url = '/rankings';
        }
        const selectedLeagueType =
            this.selectedUserLeague.league_type.charAt(0).toUpperCase() + this.selectedUserLeague.league_type.slice(1).toLowerCase();
        LocalStorage.set(`redirected${selectedLeagueType}SelectedUserLeagueId`, this.selectedUserLeagueId);
        window.location.href = url;
    },

    /**
     * Determine if we should redirect the user to another rankings page
     * based on the selected user league's league_type and draft_type_id
     * @return {boolean} True if we should redirect, false otherwise
     */
    shouldRedirectForUserLeague() {
        // If current page is weekly-rankings or ros-rankings never redirect
        if (this.basePath === 'weekly-rankings' || this.basePath === 'ros-rankings' || this.basePath == 'best-ball-rankings') {
            return false;
        }
        if (this.selectedUserLeague.draft_type_id === TYPE_ID_AUCTION && this.basePath === 'auction-values') {
            return false;
        }
        if (this.leagueType && this.selectedUserLeague.league_type.toLowerCase() !== this.leagueType.toLowerCase()) {
            return true;
        }
        return false;
    },

    resetSelectedUserLeague() {
        this.selectedUserLeagueId = null;
        this.selectedUserLeagueText = 'Use My League';
    },

    async ensureDsValueForUserLeague(leagueId) {
        if (!this.cachedLeagueData[leagueId]) {
            const response = await fetch('/league/api/v1/league/get-standard-data-for-in-season-tools?id=' + leagueId, {
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
            });
            const json = await response.json();

            // Prepare player data
            for (let i = 0; i < json.players.length; i++) {
                json.players[i] = assignMissingDefaultPlayerDataValues(json.players[i]);
            }

            // Prepare scoring data
            json.mvpBoardScoring = assignMissingDefaultMvpBoardScoringValues(getArrItemByPropValue(leagueId, this.userLeagues));

            // Debug settings
            const debugSettings = {
                mode: DEBUG_MODE_DISABLED,
                level: 1,
                categories: [],
                subcategories: [],
                playerIds: [],
                mvpBoardTeamIds: [],
                rosterSpots: [],
                ...LocalStorage.get('debugSettings', {}),
            };

            let userLeaguePlayers;

            if (this.workerManager.isReady) {
                try {
                    this.processingMessage = 'Processing in background...';
                    const backgroundOperationType =
                        this.basePath === 'auction-values' ? 'calculatePlayersValuesAndAuctionDollars' : 'calculatePlayersValues';
                    const result = await this.workerManager.executeOperation(backgroundOperationType, {
                        players: json.players,
                        mvpBoardScoring: { ...json.mvpBoardScoring },
                        playerValueCalculationSettings: json.playerValueCalculationSettings,
                        debugSettings,
                    });

                    userLeaguePlayers = result.players;
                } catch (workerError) {
                    userLeaguePlayers = await this.calculatePlayersValuesMainThread(
                        json.players,
                        json.mvpBoardScoring,
                        json.playerValueCalculationSettings,
                        debugSettings
                    );
                }
            } else {
                userLeaguePlayers = await this.calculatePlayersValuesMainThread(
                    json.players,
                    json.mvpBoardScoring,
                    json.playerValueCalculationSettings,
                    debugSettings
                );
            }

            this.cachedLeagueData[leagueId] = {
                players: userLeaguePlayers,
                leagueData: json,
                // A league without rosters would otherwise flag every player as a free agent
                hasRosters: userLeaguePlayers.some(player => player.mvp_board_player_team_mvp_board_team_id),
            };
        }

        const userLeaguePlayers = this.cachedLeagueData[leagueId].players;
        window.dsUserLeaguePlayers = userLeaguePlayers; // For debugging

        // Update the rankings table with the user league player data. Parked (windowed)
        // rows must be included - they rejoin the DOM later and would otherwise show
        // stale server-scoring values. The table is queried from the document because this also
        // runs inside the htmx:afterSwap handler, where $refs still points at the replaced table.
        const rankingsTable = document.querySelector('#rankingsTable');
        const playerRows = [...rankingsTable.querySelectorAll('tbody[data-player-row]'), ...(this._parkedRows || [])];
        for (let i = 0; i < playerRows.length; i++) {
            const playerRow = playerRows[i];
            const playerId = playerRow.dataset.key;

            // Find the corresponding player in userLeaguePlayers
            const leaguePlayer = getArrItemByPropValue(playerId, userLeaguePlayers, 'player_id', false);
            if (!leaguePlayer) {
                continue; // Skip if player not found
            }

            // Assign values to the core app display data
            this.updatePlayerRow(playerRow, leaguePlayer);
        }

        return this.cachedLeagueData[leagueId].players;
    },

    async calculatePlayersValuesMainThread(players, mvpBoardScoring, playerValueCalculationSettings, debugSettings) {
        let userLeaguePlayers;
        console.log('calculatePlayersValuesMainThread', players, mvpBoardScoring, playerValueCalculationSettings, debugSettings);
        try {
            if (this.basePath === 'auction-values') {
                const { calculatePlayersValues, calculateAuctionDollars } = await import('./../../league/wasm/BoardProcessorDebug.js');
                userLeaguePlayers = calculatePlayersValues(players, mvpBoardScoring, playerValueCalculationSettings, debugSettings);
                // --- Must remove any details about player being on teams for auction $ to calculate properly
                for (let i = 0; i < userLeaguePlayers.length; i++) {
                    const player = userLeaguePlayers[i];
                    player.mvp_board_draft_pick_mvp_board_team_id = 0;
                    player.mvp_board_player_team_mvp_board_team_id = 0;
                    player.overall_pick_number = 0;
                    player.winning_bid_amount = 0;
                }
                userLeaguePlayers = calculateAuctionDollars(mvpBoardScoring, userLeaguePlayers, debugSettings);
            } else {
                const { calculatePlayersValues } = await import('./../../league/wasm/BoardProcessorDebug.js');
                userLeaguePlayers = calculatePlayersValues(players, mvpBoardScoring, playerValueCalculationSettings, debugSettings);
            }
            exportAndPublishWasmLogEntries();
            return userLeaguePlayers;
        } catch (err) {
            console.error('Error in main thread player value calculation:', err);
            alert('An error occurred while calculating player values. Please try again later.');
            throw err;
        }
    },

    updatePlayerRow(row, leaguePlayer) {
        const resolvedFullSeasonProjectionSource = [
            leaguePlayer.fullSeasonFantasyPts,
            leaguePlayer.fantasy_points,
            Number(leaguePlayer.fantasyPtsNoReplacement || 0) + Number(leaguePlayer.replacementPts || 0),
        ].find(value => Number.isFinite(Number(value)));
        const fullSeasonProjectionPoints = Number.isFinite(Number(resolvedFullSeasonProjectionSource))
            ? Number(resolvedFullSeasonProjectionSource)
            : null;

        // Store reference to the user's league data for use in the formatter functions
        const cachedLeague = this.cachedLeagueData[this.selectedUserLeagueId];
        const userLeagueData = cachedLeague?.leagueData;

        // If leaguePlayer is owned, add class "is-mine" to row
        row.classList.remove('is-mine');
        if (leaguePlayer.is_user_owned) {
            row.classList.add('is-mine');
        }

        // On roster status pages, add class "is-free" when no team in the league has the player
        const isFreeAgent =
            Boolean(cachedLeague?.hasRosters) && !leaguePlayer.is_user_owned && !leaguePlayer.mvp_board_player_team_mvp_board_team_id;
        row.classList.toggle('is-free', this.showsRosterStatus() && isFreeAgent);
        // Must match refreshSections sort keys (tierOverall/tierPositional), not overallTier/positionTier.
        row.dataset.tierOverall = leaguePlayer.overallTier;
        row.dataset.tierPositional = leaguePlayer.positionTier;
        const attributesToUpdate = [
            {
                dsValue: 'dsValue',
                formatter: function (v) {
                    return v ? v.toFixed(0) : 0;
                },
            },
            {
                weeklyPts: 'weeklyPts',
                formatter: function (v) {
                    return v ? v.toFixed(1) : 0;
                },
            },
            {
                consensusWeeklyPts: 'consensusWeeklyPts',
                formatter: function (v) {
                    return v ? v.toFixed(1) : 0;
                },
            },
            {
                // Next week has no league 3D blend. FA's Wk N+1 3D Proj column is twoWeekPts.
                weekly3dPts:
                    this.basePath === 'weekly-rankings' && Number(this.selectedWeek) === Number(this.currentWeek) + 1
                        ? 'twoWeekPts'
                        : 'weekly3dPts',
                formatter: function (v) {
                    return v ? v.toFixed(1) : 0;
                },
            },
            // 'weeklyCeilingPts',
            // 'weeklyFloorPts',
            'floor_points',
            'rosPts',
            'consensusRosPts',
            {
                fantasy_points: 'fantasy_points',
                formatter: function (v) {
                    return Number(v || 0).toFixed(0);
                },
            },
            'threeYrPts',
            'fiveYrPts',
            'tenYrPts',
            'ros3dPts',
            // 'rosCeilingPts',
            // 'rosFloorPts',
            {
                consensusRosWeeklyPts: 'consensusRosWeeklyPts',
                formatter: function (v) {
                    return v ? v.toFixed(1) : 0;
                },
            },
            {
                rosWeeklyPts: 'rosWeeklyPts',
                formatter: function (v) {
                    return v ? v.toFixed(1) : 0;
                },
            },
            {
                rosWeekly3dPts: 'rosWeekly3dPts',
                formatter: function (v) {
                    return v ? v.toFixed(1) : 0;
                },
            },
            {
                auctionMarketValue: 'auctionMarketValue',
                formatter: function (v) {
                    return `$${v}`;
                },
            },
            {
                dsAuctionValue: 'auctionDollars',
                formatter: function (v) {
                    return `$${v}`;
                },
            },
            {
                adp: 'average_adp_overall',
                formatter: function (v) {
                    return getPickDisplay(v, userLeagueData?.mvpBoardTeams.length);
                },
            },
        ];

        let updatedFloorAndCeilPts = false;
        attributesToUpdate.forEach(attribute => {
            let cellAttribute = typeof attribute === 'string' ? attribute : Object.keys(attribute)[0];
            let leaguePlayerAttribute = typeof attribute === 'string' ? attribute : Object.values(attribute)[0];
            let cellValueFormatter = typeof attribute === 'string' ? null : attribute['formatter'] || null;
            try {
                const cell = row.querySelector(`[data-attribute="${cellAttribute}"]`);
                if (!cell) {
                    return;
                }
                const leaguePlayerAttributeValue =
                    leaguePlayerAttribute === 'fantasy_points'
                        ? (fullSeasonProjectionPoints ?? Number(cell.dataset.value || 0))
                        : leaguePlayer[leaguePlayerAttribute];
                const cellValue =
                    typeof cellValueFormatter === 'function' ? cellValueFormatter(leaguePlayerAttributeValue) : leaguePlayerAttributeValue;
                this.updatePlayerCell(cell, leaguePlayerAttributeValue?.toFixed(2) || 0.0, cellValue);

                // If updating weeklyPts, or rosWeeklyPts, also calcuate and update the floor and ceiling cells
                // for weeklyPts, update weeklyCeilingPts and weeklyFloorPts
                // for rosWeeklyPts, update rosWeeklyCeilingPts and rosWeeklyFloorPts
                if (leaguePlayerAttribute === 'weeklyPts' || leaguePlayerAttribute === 'rosWeeklyPts') {
                    const fantasyPoints = leaguePlayerAttribute === 'weeklyPts' ? leaguePlayer.weeklyPts : leaguePlayer.rosWeeklyPts;
                    if (!fantasyPoints || fantasyPoints <= 0) {
                        return;
                    }

                    const ceilingCellAttribute = leaguePlayerAttribute === 'weeklyPts' ? 'weeklyCeilingPts' : 'rosWeeklyCeilingPts';
                    const floorCellAttribute = leaguePlayerAttribute === 'weeklyPts' ? 'weeklyFloorPts' : 'rosWeeklyFloorPts';

                    const ceilingCell = row.querySelector(`[data-attribute="${ceilingCellAttribute}"]`);
                    if (ceilingCell) {
                        const ceilingPoints = this.dsHelper.getCeilingPoints(fantasyPoints, +row.dataset.percentHigh);
                        ceilingCell.setAttribute('data-value', ceilingPoints.toFixed(2));
                        ceilingCell.innerHTML = `<span class="column-title ds-pill" :class="getCeilingClass('${row.dataset.percentHigh}', '${
                            row.dataset.fantasyPosition
                        }')">${ceilingPoints.toFixed(1)}</span>`;
                        updatedFloorAndCeilPts = true;
                    }
                    const floorCell = row.querySelector(`[data-attribute="${floorCellAttribute}"]`);
                    if (floorCell) {
                        const floorPoints = this.dsHelper.getFloorPoints(fantasyPoints, +row.dataset.percentLow);
                        floorCell.setAttribute('data-value', floorPoints.toFixed(2));
                        floorCell.innerHTML = `<span class="column-title ds-pill" :class="getFloorClass('${row.dataset.percentLow}', '${
                            row.dataset.fantasyPosition
                        }')">${floorPoints.toFixed(1)}</span>`;
                        updatedFloorAndCeilPts = true;
                    }
                }
            } catch (error) {
                console.error(
                    `Error updating cell for cellAttribute ${cellAttribute} and leaguePlayerAttribute: ${leaguePlayerAttribute}`,
                    error
                );
            }
        });

        // Update floor and ceiling individual cells
        if (!updatedFloorAndCeilPts) {
            const baseProjectionPoints =
                fullSeasonProjectionPoints ??
                Number(row.querySelector('[data-attribute="fantasy_points"]')?.getAttribute('data-value') || 0);
            const floorCell = row.querySelector('[data-attribute="floor_points"]');
            if (floorCell) {
                const floorPoints = this.dsHelper.getFloorPoints(baseProjectionPoints, row.dataset.percentLow);
                floorCell.setAttribute('data-value', floorPoints.toFixed(2));
                floorCell.innerHTML = `<span class="column-title ds-pill" :class="getFloorClass('${row.dataset.percentLow}', '${
                    row.dataset.fantasyPosition
                }')">${floorPoints.toFixed(0)}</span>`;
            }

            const ceilingCell = row.querySelector('[data-attribute="ceiling_points"]');
            if (ceilingCell) {
                const ceilingPoints = this.dsHelper.getCeilingPoints(baseProjectionPoints, row.dataset.percentHigh);
                ceilingCell.setAttribute('data-value', ceilingPoints.toFixed(2));
                ceilingCell.innerHTML = `<span class="column-title ds-pill" :class="getCeilingClass('${
                    row.dataset.percentHigh
                }', '${row.dataset.fantasyPosition}')">${ceilingPoints.toFixed(0)}</span>`;
            }
        }

        // DOM yards and touchdowns from league return rates
        const returningCell = row.querySelector('[data-attribute="returningTotal"]');
        const scoring = this.cachedLeagueData[this.selectedUserLeagueId]?.leagueData?.mvpBoardScoring;
        if (returningCell && scoring) {
            const prYds = Number(row.querySelector('[data-attribute="pr_yds_total"]')?.dataset.value || 0);
            const krYds = Number(row.querySelector('[data-attribute="kr_yds_total"]')?.dataset.value || 0);
            const returnTds = Number(row.querySelector('[data-attribute="return_touchdowns"]')?.dataset.value || 0);
            const prPer = Number(scoring.punt_return_yds_per_pt) || 0;
            const krPer = Number(scoring.kick_return_yds_per_pt) || 0;
            const total =
                (prPer > 0 ? prYds / prPer : 0) + (krPer > 0 ? krYds / krPer : 0) + returnTds * (Number(scoring.def_special_td_pts) || 0);
            this.updatePlayerCell(returningCell, total.toFixed(2), total.toFixed(0));
        }

        // update position rank
        const positionRankCell = row.querySelector('div.position-rank');
        if (positionRankCell) {
            // prettier-ignore
            const rankVal = appData.basePath == 'weekly-rankings'
                    ? leaguePlayer.rankFantasyPositionWeekly3dPts
                    : leaguePlayer.rankFantasyPositionDsValue;
            positionRankCell.textContent = `${leaguePlayer.position}${rankVal}` || '';
        }
    },

    updatePlayerCell(cell, attributeValue, cellValue) {
        cell.setAttribute('data-value', attributeValue);
        cell.innerHTML = `<span class="cell-value">${cellValue}</span>`;
    },
});
